import { useCallback, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "mp-shade-order";

const read = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    console.debug("Saved order unreadable, starting empty:", e);
    return [];
  }
};

export const keyFor = (item) =>
  item.cut_length
    ? `${item.quality_id}|cut|${item.cut_length}`
    : `${item.quality_id}|${item.number}`;

export const useShadeOrder = () => {
  const [items, setItems] = useState(read);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (e) {
      console.debug("Order could not be persisted:", e);
    }
  }, [items]);

  const add = useCallback((item) => {
    const key = keyFor(item);
    setItems((prev) => {
      const found = prev.find((i) => i.key === key);
      if (found) {
        return prev.map((i) =>
          i.key === key ? { ...i, thans: i.thans + (item.thans || 1) } : i
        );
      }
      return [...prev, { ...item, key, thans: item.thans || 1 }];
    });
  }, []);

  const setThans = useCallback((key, thans) => {
    setItems((prev) =>
      thans <= 0
        ? prev.filter((i) => i.key !== key)
        : prev.map((i) => (i.key === key ? { ...i, thans } : i))
    );
  }, []);

  const remove = useCallback((key) => {
    setItems((prev) => prev.filter((i) => i.key !== key));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const totals = useMemo(() => {
    const fabric = items.filter((i) => !i.cut_length);
    const cuts = items.filter((i) => i.cut_length);
    const thans = fabric.reduce((s, i) => s + i.thans, 0);
    const meters = fabric.reduce((s, i) => s + i.thans * i.than_meters, 0);
    const packs = cuts.reduce((s, i) => s + i.thans, 0);
    const pieces = cuts.reduce((s, i) => s + i.thans * (i.pieces_per_pack || 50), 0);
    // Parcel count and transport are worked out by the shop at billing, not here
    return { shades: items.length, thans, meters, packs, pieces };
  }, [items]);

  const isPinned = useCallback(
    (qualityId, number) => items.some((i) => i.key === `${qualityId}|${number}`),
    [items]
  );

  return { items, add, setThans, remove, clear, totals, isPinned };
};
